"use client";

import Link from "next/link";
import type { MouseEvent } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FaArrowDown, FaArrowRight, FaStar } from "react-icons/fa";
import { LogoTicker } from "@/components/logo-ticker";
import { HeroDashboardCards } from "@/components/sections/hero-dashboard-cards";
import { HeroCtaButton } from "@/components/ui/hero-cta-button";
import {
  createHeroEntranceVariants,
  HERO_CONTENT_ENTRANCE,
  HERO_ENTRANCE_PERSPECTIVE,
  HERO_ENTRANCE_PERSPECTIVE_ORIGIN,
  HERO_ENTRANCE_SCROLL_DELAY,
  HERO_ENTRANCE_SHINE_DELAY,
  HERO_TICKER_ENTRANCE,
} from "@/lib/hero-entrance-motion";

function HeroTagEntry() {
  return (
    <div className="flex flex-col items-center">
      <p className="relative isolate inline-flex items-center gap-2.5 overflow-hidden rounded-full border border-emerald-700/30 bg-emerald-600 px-4 py-1.5 text-sm shadow-[0_4px_14px_rgba(5,150,105,0.28)] sm:gap-3 sm:px-5 sm:py-2">
        <span aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden rounded-full">
          <span
            className="hero-cta-metal-shine-track absolute -inset-y-4 left-0 h-[calc(100%+2rem)] w-[42%] bg-[linear-gradient(105deg,transparent_0%,rgba(255,255,255,0.04)_28%,rgba(255,255,255,0.2)_44%,rgba(255,255,255,0.45)_50%,rgba(255,255,255,0.2)_56%,rgba(255,255,255,0.04)_72%,transparent_100%)]"
            style={{ animationDelay: HERO_ENTRANCE_SHINE_DELAY }}
          />
        </span>
        <span className="relative z-10 flex items-center gap-0.5" aria-hidden="true">
          {Array.from({ length: 5 }).map((_, index) => (
            <FaStar key={index} className="text-[11px] text-white sm:text-xs" />
          ))}
        </span>
        <span className="relative z-10 h-4 w-px bg-white/35 sm:h-5" aria-hidden="true" />
        <span className="relative z-10 font-semibold text-white">+20 projets livrés</span>
      </p>
    </div>
  );
}

export function HeroSection() {
  const reducedMotion = useReducedMotion();

  const handleScrollToPortfolio = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const portfolioSection = document.getElementById("portfolio");
    if (portfolioSection) {
      const headerOffset = 150;
      const elementPosition = portfolioSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <section className="relative -mt-[calc(5rem+env(safe-area-inset-top))] flex min-h-[calc(100svh-2rem)] flex-col overflow-hidden bg-[#f3f3f3] text-[#1C1C1C] antialiased">
      <div className="relative z-10 mx-auto flex w-full min-h-0 max-w-5xl flex-1 flex-col px-5 pt-[calc(5rem+env(safe-area-inset-top)+1.5rem)] pb-8 text-center sm:px-8 sm:pt-[calc(5rem+env(safe-area-inset-top)+2rem)] xl:max-w-7xl">
        <div className="flex min-h-0 flex-1 flex-col items-center justify-end">
          <div
            className="relative mx-auto w-full min-h-[min(420px,48vh)] max-w-6xl overflow-visible px-2 md:min-h-[min(460px,52vh)] md:px-4 xl:max-w-7xl xl:min-h-[min(500px,56vh)] xl:px-6"
            style={{ perspective: HERO_ENTRANCE_PERSPECTIVE, perspectiveOrigin: HERO_ENTRANCE_PERSPECTIVE_ORIGIN }}
          >
            <HeroDashboardCards />
            <motion.div
              className="relative z-10 mx-auto flex w-full max-w-3xl flex-col items-center gap-7 overflow-visible sm:gap-8"
              custom={HERO_CONTENT_ENTRANCE}
              initial="hidden"
              animate="visible"
              variants={createHeroEntranceVariants(!!reducedMotion)}
              style={{ transformStyle: "preserve-3d", transformOrigin: "50% 100%" }}
            >
              <HeroTagEntry />

            <h1 className="max-w-4xl text-balance text-[2rem] font-bold leading-[1.08] tracking-[-0.04em] text-[#1C1C1C] sm:text-5xl md:text-6xl md:leading-[1.05]">
              Votre{" "}
              <span className="bg-gradient-to-r from-emerald-600 via-teal-600 to-green-600 bg-clip-text text-transparent">
                site
              </span>{" "}
              vous{" "}
              <span className="bg-gradient-to-r from-emerald-600 via-teal-600 to-green-600 bg-clip-text text-transparent">
                coûte
              </span>{" "}
              des{" "}
              <span className="bg-gradient-to-r from-emerald-600 via-teal-600 to-green-600 bg-clip-text text-transparent">
                clients
              </span>{" "}
              chaque mois.
            </h1>

            <p className="max-w-2xl text-pretty text-base leading-relaxed text-zinc-500 sm:text-lg">
              On crée des sites web sur-mesure pour les entrepreneurs et PME qui veulent arrêter de perdre des
              prospects et commencer à les convertir automatiquement.
            </p>

            <div className="flex flex-col items-center gap-4 pt-1">
              <HeroCtaButton shineDelay={HERO_ENTRANCE_SHINE_DELAY}>
                Réserver mon audit gratuit
              </HeroCtaButton>

              <Link
                href="#portfolio"
                onClick={handleScrollToPortfolio}
                className="inline-flex items-center gap-2 text-sm font-medium text-zinc-500 transition hover:text-[#1C1C1C]"
              >
                Voir nos projets
                <FaArrowDown className="text-xs" aria-hidden="true" />
              </Link>
            </div>
            </motion.div>
          </div>
        </div>

        <div
          className="flex min-h-0 flex-1 w-full items-center justify-center"
          style={{ perspective: HERO_ENTRANCE_PERSPECTIVE, perspectiveOrigin: HERO_ENTRANCE_PERSPECTIVE_ORIGIN }}
        >
          <motion.div
            className="w-full"
            custom={HERO_TICKER_ENTRANCE}
            initial="hidden"
            animate="visible"
            variants={createHeroEntranceVariants(!!reducedMotion)}
            style={{ transformStyle: "preserve-3d", transformOrigin: "50% 100%" }}
          >
            <LogoTicker scrollDelay={HERO_ENTRANCE_SCROLL_DELAY} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
